<template>
  <div :class="['navbar-area style-one position-relative z-2', { sticky: isSticky }]" id="navbar">
    <div class="container-fluid">
      <div class="navbar-wrapper d-flex align-items-xl-end align-items-center">
        <router-link to="/" class="navbar-brand">
          <img :src="logoLight" alt="Logo" />
        </router-link>

        <div class="menu-wrapper d-flex align-items-center ms-auto">
          <div class="menu-area">
            <div
              class="overlay"
              :class="{ active: isMobileMenuOpen }"
              @click="closeMobileMenu"
            ></div>
            <nav class="menu" :class="{ active: isMobileMenuOpen }">
              <div class="menu-mobile-header">
                <button
                  type="button"
                  class="menu-mobile-arrow bg-transparent border-0"
                  @click="hideSubMenu"
                >
                  <i class="ri-arrow-left-s-line"></i>
                </button>
                <div class="menu-mobile-title"></div>
                <button
                  type="button"
                  class="menu-mobile-close bg-transparent border-0"
                  @click="closeMobileMenu"
                >
                  <i class="ri-close-line"></i>
                </button>
              </div>

              <ul class="menu-section p-0 mb-0 lh-1">
                <!-- Home Link -->
                <li>
                  <router-link to="/" @click="closeMobileMenu" exact-active-class="active">
                    Home
                  </router-link>
                </li>
                
                <!-- Dynamic Pages from CMS -->
                <li v-for="page in pages" :key="page.id">
                  <router-link 
                    :to="`/pages/${page.slug}`" 
                    @click="closeMobileMenu"
                    active-class="active"
                  >
                    {{ page.title }}
                  </router-link>
                </li>
                
                <!-- Blog Link -->
                <li>
                  <router-link to="/blog" @click="closeMobileMenu" active-class="active">
                    Blog
                  </router-link>
                </li>
                
                <!-- Contact Link -->
                <li>
                  <router-link to="/contact" @click="closeMobileMenu" active-class="active">
                    Contact
                  </router-link>
                </li>
              </ul>
            </nav>
          </div>
          
          <div class="other-options d-flex flex-wrap align-items-center justify-content-end">
            <div class="option-item position-relative">
              <button
                class="search-btn border-0 d-flex flex-column align-items-center justify-content-center dropdown-toggle text-white rounded-circle transition"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i class="ri-search-line"></i>
                <i class="ri-close-line"></i>
              </button>
              <div
                class="search-dropdown dropdown-menu dropdown-menu-right top-1 border-0"
              >
                <form class="search-popup position-relative" @submit.prevent="handleSearch">
                  <input 
                    v-model="searchQuery" 
                    type="search" 
                    class="form-control text-para" 
                    placeholder="Search Here" 
                  />
                  <button
                    type="submit"
                    class="position-absolute top-0 end-0 h-100 border-0 bg-transparent d-flex flex-column align-items-center justify-content-center"
                  >
                    <i class="ri-search-2-line"></i>
                  </button>
                </form>
              </div>
            </div>
            
            <div class="option-item">
              <button
                type="button"
                data-bs-toggle="offcanvas"
                data-bs-target="#sidebar-area"
                aria-controls="sidebar-area"
                class="sidebar-btn p-0 d-flex flex-column align-items-center justify-content-center rounded-circle text-white transition border-0"
              >
                <i class="ri-menu-3-fill"></i>
              </button>
            </div>
            
            <div class="option-item d-lg-none">
              <button type="button" class="menu-mobile-trigger" @click="toggleMobileMenu">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Sidebar (Offcanvas) -->
  <div 
    class="sidebar-area offcanvas offcanvas-end" 
    tabindex="-1" 
    id="sidebar-area" 
    aria-labelledby="sidebar-areaLabel"
  >
    <div class="offcanvas-header">
      <router-link to="/" class="logo">
        <img :src="logoLight" alt="Logo" />
      </router-link>
      <button 
        type="button" 
        class="btn-close text-white" 
        data-bs-dismiss="offcanvas" 
        aria-label="Close"
      ></button>
    </div>
    <div class="offcanvas-body position-relative z-1">
      <div class="sidebar-content">
        <p class="text-white mb-25">{{ siteDescription }}</p>
        
        <ul class="contact-info list-unstyled mb-30">
          <li v-if="address" class="position-relative mb-20">
            <i class="ri-map-pin-2-fill"></i>
            <h6 class="text-white fw-bold mb-1">Location</h6>
            <p class="text-white mb-0">{{ address }}</p>
          </li>
          <li v-if="contactEmail" class="position-relative mb-20">
            <i class="ri-mail-fill"></i>
            <h6 class="text-white fw-bold mb-1">Email</h6>
            <a :href="`mailto:${contactEmail}`" class="text-white link-hover-secondary transition">
              {{ contactEmail }}
            </a>
          </li>
          <li v-if="contactPhone" class="position-relative mb-20">
            <i class="ri-phone-fill"></i>
            <h6 class="text-white fw-bold mb-1">Phone</h6>
            <a :href="`tel:${contactPhone.replace(/\D/g, '')}`" class="text-white link-hover-secondary transition">
              {{ contactPhone }}
            </a>
          </li>
        </ul>
        
        <div class="comp-map round-20 mb-30" v-if="mapEmbedUrl">
          <iframe
            :src="mapEmbedUrl"
            style="border: 0; width: 100%; height: 100%"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useSiteDefaultsStore } from '@/stores/siteDefaultsStore'

export default {
    name: 'AppHeader',
    data() {
        return {
            isSticky: false,
            isMobileMenuOpen: false,
            searchQuery: '',
            pages: []
        }
    },
    computed: {
        siteDefaultsStore() {
            return useSiteDefaultsStore()
        },
        logoLight() {
            return this.siteDefaultsStore.getLogoLight() || '/assets/img/logo-white.png'
        },
        siteName() {
            return this.siteDefaultsStore.getSiteName() || 'Olavo'
        },
        siteDescription() {
            return this.siteDefaultsStore.getSiteDescription() || 'Expert legal counsel'
        },
        contactPhone() {
            return this.siteDefaultsStore.getContactPhone() || ''
        },
        contactEmail() {
            return this.siteDefaultsStore.getContactEmail() || ''
        },
        address() {
            return this.siteDefaultsStore.getAddress() || ''
        },
        socialLinks() {
            return this.siteDefaultsStore.getSocialLinks() || []
        },
        mapEmbedUrl() {
            const lat = this.siteDefaultsStore.getMapLat()
            const lng = this.siteDefaultsStore.getMapLng()
            
            // If coordinates are available, use them
            if (lat && lng && lat !== '0' && lng !== '0') {
                return `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=${lat},${lng}&zoom=15`
            }
            
            // Fallback to default location
            return 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.8385385572983!2d144.95358331584498!3d-37.81725074201705!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad65d4dd5a05d97%3A0x3e64f855a564844d!2s121%20King%20St%2C%20Melbourne%20VIC%203000%2C%20Australia!5e0!3m2!1sen!2sbd!4v1612419490850!5m2!1sen!2sbd'
        }
    },
    async mounted() {
        window.addEventListener('scroll', this.handleScroll)
        window.addEventListener('resize', this.handleResize)
        
        // Load pages for navigation
        await this.loadPages()
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.handleScroll)
        window.removeEventListener('resize', this.handleResize)
    },
    watch: {
        '$route'() {
            this.closeMobileMenu()
        }
    },
    methods: {
        async loadPages() {
            try {
                const response = await this.$api.get('public/pages/list')
                if (response.data && response.data.status === 'success') {
                    this.pages = response.data.data.sort((a, b) => a.sort_order - b.sort_order)
                }
            } catch (error) {
                console.error('Failed to load pages:', error)
                this.pages = []
            }
        },
        
        handleScroll() {
            this.isSticky = window.scrollY >= 100
        },
        
        handleResize() {
            if (window.innerWidth > 991 && this.isMobileMenuOpen) {
                this.closeMobileMenu()
            }
        },
        
        toggleMobileMenu() {
            this.isMobileMenuOpen = !this.isMobileMenuOpen
        },
        
        closeMobileMenu() {
            this.isMobileMenuOpen = false
        },
        
        hideSubMenu() {
            // For compatibility with original theme structure
            // Not needed for flat navigation
        },
        
        handleSearch() {
            if (this.searchQuery.trim()) {
                // Implement search functionality
                console.log('Search for:', this.searchQuery)
                // You can navigate to a search page or perform search
                this.searchQuery = ''
            }
        }
    }
}
</script>

<style scoped>
.sidebar-area {
    background: #303E4D !important;
}

.sidebar-area .offcanvas-header {
    padding: 30px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-area .offcanvas-body {
    padding: 30px;
}

.sidebar-area .close-btn {
    background: white;
    width: 40px;
    height: 40px;
    font-size: 20px;
}

.sidebar-area .contact-info li {
    padding-left: 45px;
}

.sidebar-area .contact-info li i {
    position: absolute;
    left: 0;
    top: 0;
    width: 35px;
    height: 35px;
    background: rgba(102, 152, 200, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6698C8;
    font-size: 18px;
}

.sidebar-area .contact-info a:hover {
    color: #6698C8 !important;
}

.sidebar-area .comp-map {
    height: 250px;
    overflow: hidden;
}

@media (max-width: 767px) {
    .sidebar-area .offcanvas-header,
    .sidebar-area .offcanvas-body {
        padding: 20px;
    }
}
</style>
